import { registerLocaleData } from '@angular/common';
import localeSlovenian from '@angular/common/locales/sl';
import { Injectable, LOCALE_ID, inject } from '@angular/core';
import { Store } from '@ngxs/store';
import dayjs from 'dayjs';
import { environment } from '../../../environments/environment';
import { BaseStateActions } from '../state/base/base.actions';
import { BaseSelectors } from '../state/base/base.selectors';

@Injectable({
    providedIn: 'root',
})
export class LanguageService {
    private locale = inject(LOCALE_ID);
    private store = inject(Store);

    private static redirecting = false;

    public switchLanguage(newLanguage: string): boolean {
        const currentUrl = new URL(window.location.href);
        const segments = currentUrl.pathname.split('/');

        let localeSegmentIndex = segments.findIndex(segment => segment === this.locale);
        if (localeSegmentIndex < 0) {
            // Fallback for setups where LOCALE_ID does not match the path segment format.
            localeSegmentIndex = segments.findIndex(segment => segment === 'sl' || segment === 'en');
        }

        if (localeSegmentIndex < 0) {
            return false;
        }

        segments[localeSegmentIndex] = newLanguage;
        const nextPath = segments.join('/');
        const nextUrl = `${currentUrl.origin}${nextPath}${currentUrl.search}${currentUrl.hash}`;

        if (nextUrl === currentUrl.href) {
            return false;
        }

        this.store.dispatch([new BaseStateActions.Common.SetLanguage(newLanguage)]);
        window.location.href = nextUrl;
        return true;
    }

    public validateOrRedirectRoute(): void {
        const languageInStore = this.store.selectSnapshot(BaseSelectors.getLanguage);
        if (languageInStore && languageInStore !== this.locale) {
            const supportedLanguages = environment.languageSettings.languages.map(language => language.value);
            const languageMatch = supportedLanguages?.find(language => language === languageInStore);
            if (languageMatch && !LanguageService.redirecting) {
                LanguageService.redirecting = true;
                const redirected = this.switchLanguage(languageInStore);
                if (!redirected) {
                    // Keep app stable in non-localized URL setups where path redirection is not possible.
                    this.store.dispatch([new BaseStateActions.Common.SetLanguage(this.locale)]);
                    LanguageService.redirecting = false;
                }
            }
        }
    }

    public initLocale() {
        // Should date format change together with language?
        // Language and regional settings should be separated but in that case, month names will be wrong.
        dayjs.locale(this.locale);
        registerLocaleData(localeSlovenian, 'sl');
    }
}
